import { usePrevious } from '@lemonade-hq/cdk';
import type { Milliseconds } from '@lemonade-hq/ts-helpers';
import { useDelayedValue } from '@lemonade-hq/uniclient-theme';
import { motion } from 'motion/react';
import { getLineAnimationState } from '../animationHelpers';
import { lineVariants } from '../animations';
import type { AnimationProgressProps, BaseProgressItemProps } from '../types';

const MID_TRANSITION_DELAY: Milliseconds = 500;
const LONG_TRANSITION_DELAY: Milliseconds = 1400;

export const Line: React.FC<
  Omit<AnimationProgressProps, 'isExpanding'> &
    Omit<BaseProgressItemProps, 'current'> & {
      readonly isLastItem: boolean;
    }
> = ({
  completed,
  currentSubItemIndex = -1,
  numOfSubItems = 0,
  level = 1,
  numOfSubSubItems = 0,
  currentSubSubItemIndex = -1,
  isBackwards,
  isLastItem,
}) => {
  const delay = numOfSubItems > 0 ? (completed ? MID_TRANSITION_DELAY : LONG_TRANSITION_DELAY) : MID_TRANSITION_DELAY;
  const delayedCompleted = useDelayedValue(completed, completed, delay);
  // Collapsing
  const previousSubSubItemIndex = usePrevious(currentSubSubItemIndex, currentSubSubItemIndex);
  const isSubItemCollapsing = previousSubSubItemIndex > -1 && currentSubSubItemIndex === -1;
  const delayedIsSubItemCollapsing = useDelayedValue(
    isSubItemCollapsing,
    isSubItemCollapsing,
    isSubItemCollapsing ? 0 : 300,
  );

  // Expanding
  const isFirstSubItem = currentSubItemIndex === 0;
  const isFirstSubSubItem = currentSubSubItemIndex === 0;
  const isExpanding = level === 3 ? isFirstSubSubItem : isFirstSubItem;

  return (
    <motion.div
      animate={getLineAnimationState(completed, delayedCompleted, currentSubItemIndex)}
      custom={{
        currentSubItemIndex: delayedIsSubItemCollapsing ? currentSubItemIndex - 1 : currentSubItemIndex,
        numOfSubItems,
        level,
        numOfSubSubItems,
        currentSubSubItemIndex,
        isExpanding,
        isBackwards,
        isLastItem,
      }}
      initial={false}
      variants={lineVariants}
    />
  );
};
